import React from "react";
import { Box, ButtonDropdown } from "@amzn/awsui-components-react";
import { Investigation, Stage } from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useSetDocumentRedriveReminder,
} from "../../hooks/useInvestigations";

type ReminderOption = "in-1h" | "tomorrow";

/** When each option fires, in the viewer's local time ("tomorrow" = 09:00 tomorrow). */
export const reminderTime = (
  option: ReminderOption,
  now: Date = new Date()
): Date => {
  if (option === "in-1h") return new Date(now.getTime() + 60 * 60 * 1000);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  return tomorrow;
};

/** "17:25" (today) or "Tue 09:00" for a pending reminder. */
export const reminderLabel = (at: Date, now: Date = new Date()): string => {
  const time = at.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const days = Math.round(
    (new Date(at).setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) /
      86_400_000
  );
  // Short enough for the narrow stepper column: "17:25" today, else "Tue 09:00".
  if (days === 0) return time;
  return `${at.toLocaleDateString([], { weekday: "short" })} ${time}`;
};

interface RemindMeButtonProps {
  stage: Stage;
  investigation: Investigation;
  onError: (message: string) => void;
}

/** The pending reminder's time, or null (none, failed, or already past). */
export const activeReminder = (investigation: Investigation): Date | null => {
  const reminder = investigation.documentRedrive?.reminder;
  if (!reminder?.remindAt || reminder.status === "FAILED") return null;
  const at = new Date(reminder.remindAt);
  return at.getTime() > Date.now() ? at : null;
};

/** Quiet one-line note under the actions: "Reminder Tue 09:00". Nothing when none. */
export function ReminderNote({
  investigation,
}: {
  investigation: Investigation;
}) {
  const at = activeReminder(investigation);
  if (!at) return null;
  return (
    <Box
      color="text-body-secondary"
      fontSize="body-s"
      data-testid="reminder-note"
    >
      <span style={{ whiteSpace: "nowrap" }}>Reminder {reminderLabel(at)}</span>
    </Box>
  );
}

/**
 * "Remind me" for the owner's CBE check: a bell icon dropdown beside the step's button (fits the
 * narrow stepper column; no text button). Picking again replaces the reminder. Emails + Slacks
 * the owner. Pair with <ReminderNote> to show when it is set.
 */
export function RemindMeButton({
  stage,
  investigation,
  onError,
}: RemindMeButtonProps) {
  const setReminder = useSetDocumentRedriveReminder(stage);
  const { documentRedrive: doc } = investigation;
  if (!doc?.attemptId) return null;
  const active = activeReminder(investigation);

  return (
    <ButtonDropdown
      data-testid="remind-me"
      variant="icon"
      iconName="notification"
      loading={setReminder.isLoading}
      ariaLabel={
        active
          ? `Reminder set for ${reminderLabel(active)}; change it`
          : "Remind me to check the CBEs later"
      }
      items={[
        { id: "in-1h", text: "Remind me in 1 hour" },
        { id: "tomorrow", text: "Remind me tomorrow at 09:00" },
      ]}
      onItemClick={({ detail }) => {
        setReminder
          .mutateAsync({
            investigationId: investigation.id,
            expectedVersion: investigation.version,
            attemptId: doc.attemptId as string,
            remindAt: reminderTime(detail.id as ReminderOption).toISOString(),
          })
          .catch((err: InvestigationApiError) => onError(err.message));
      }}
    />
  );
}
